import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppSumoRedeem from "./AppSumoRedeem";
import useSkipDetails from "../../hooks/useSkipDetails";
import { getAgentsByUserIdforkey } from "../../Store/apiStore";

/**
 * Route wrapper for the AppSumo redeem step.
 *
 * This screen deliberately has its own route rather than rendering inside
 * SignUp: verifying the OTP stores the token, which flips App.js's /signup
 * route to a <Navigate> and unmounts SignUp in that same render. Anything held
 * in SignUp's state would be lost at exactly the moment we need to show it.
 *
 * SignUp passes the routing decision it computed from the OTP response through
 * location.state.afterRedeem; we replay it here once the user finishes or
 * skips. Landing here directly (a refresh, or a shared URL) has no such state,
 * so we fall back to resolving the destination from the account itself.
 */
const AppSumoRedeemPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { skipToBusinessStep } = useSkipDetails();

  const afterRedeem = location.state?.afterRedeem || null;

  // A redeem that succeeds and a skip both land in continueOnboarding. Guard
  // against it running twice (double click, or a re-render mid-navigation).
  const movedOn = useRef(false);

  // No token means the OTP step never completed — this route is only reachable
  // as part of signup, so send them back rather than showing a redeem form that
  // cannot authenticate.
  useEffect(() => {
    console.log(
      "[AppSumo] RedeemPage MOUNT — token:",
      !!localStorage.getItem("token"),
      "state:",
      afterRedeem
    );
    if (!localStorage.getItem("token")) {
      console.log("[AppSumo] RedeemPage — no token, bouncing to /signup");
      navigate("/signup", { replace: true });
    }
  }, [navigate, afterRedeem]);

  const continueOnboarding = useCallback(async (redeemed) => {
    if (movedOn.current) return;
    movedOn.current = true;

    // Releases the /signup route guard in App.js. Cleared here rather than on
    // mount so that a refresh of this page still lands back on the redeem
    // screen instead of being bounced onward by that guard.
    localStorage.removeItem("appsumoRedeemPending");

    // Hand the grant to the next screen, which announces it in a popup — the
    // redeem screen no longer has a confirmation page of its own. sessionStorage
    // rather than navigation state because the route that follows is decided
    // below and, on the skipToBusinessStep path, is several redirects away.
    if (redeemed?.success) {
      try {
        sessionStorage.setItem(
          "appsumoRedeemed",
          JSON.stringify({
            minutes: redeemed.minutesGranted,
            planName: redeemed.planName,
          })
        );
      } catch {
        /* storage blocked — the popup is a nicety, not the grant itself */
      }
    }

    // Replay SignUp's decision when we have it.
    if (afterRedeem) {
      const { isPaymentDone, isOnboardComplete, agentId, businessId } = afterRedeem;

      if (!isOnboardComplete) {
        skipToBusinessStep({ replace: true });
        return;
      }
      if (isPaymentDone) {
        navigate("/steps", { replace: true });
        return;
      }
      if (agentId) {
        sessionStorage.setItem("SelectAgentId", agentId);
        sessionStorage.setItem("SelectAgentBusinessId", businessId || "");
      }
      navigate("/fast-agent-detail", { replace: true });
      return;
    }

    // Direct landing (refresh): onboardComplete is the same flag SignUp read off
    // the verify response, kept in localStorage by that flow.
    if (localStorage.getItem("onboardComplete") === "true") {
      // One agent → straight to it, so the user isn't dropped on a picker.
      try {
        const resp = await getAgentsByUserIdforkey();
        if (resp?.count === 1 && resp.agent) {
          sessionStorage.setItem("SelectAgentId", resp.agent.agent_id);
          sessionStorage.setItem("SelectAgentBusinessId", resp.agent.businessId || "");
        }
      } catch {
        /* soft-fail — FastAgentDetails resolves the agent on its own */
      }
      navigate("/fast-agent-detail", { replace: true });
      return;
    }

    skipToBusinessStep({ replace: true });
  }, [afterRedeem, navigate, skipToBusinessStep]);

  return <AppSumoRedeem onDone={continueOnboarding} />;
};

export default AppSumoRedeemPage;
